import React, {
  createContext,
  useContext,
  useMemo,
  useEffect,
  useState,
} from 'react';
import { useFormPermissions } from './FormPermissionProvider';
import addFormSchemaEssentials from './lib/addFormSchemaEssentials';
import getMissingFields from './lib/getMissingFields';
import getYupLeafFieldPaths from '../../helpers/getYupFieldPaths';
import getNamesFromSchema from '../../helpers/getNamesFromSchema';
import { useFormSchema } from './FormSchemaProvider';
import { useFormDefaultValues } from './FormDefaultValuesProvider';
import { useFormDebug } from './FormDebugProvider';
import FormProviderRenderer from './FormProviderRenderer';
import { useFormValidationSchema } from './FormValidationProvider';

const FormComposerContext = createContext({});

/**
 * FormComposerProvider is a React context provider that composes and manages form schema,
 * validation, permissions, and default values for advanced forms.
 *
 * Props:
 * - children: React nodes to render within the provider.
 * - validationSchema: Yup validation schema for the form.
 * - userAccess: User access information for permission checks.
 *
 * Context Value:
 * - validations: The processed Yup validation schema.
 * - schema: The composed form schema with essentials and permissions.
 * - defaultValues: Default values for the form fields.
 * - permissions: Permissions for the current user.
 *
 * Handles loading and error states, and ensures the form context is initialized
 * with the correct schema and validation logic.
 */

const INITIAL_STATE = {
  errors: [],
  loading: true,
  validations: null,
  schema: null,
};

export function FormComposerProvider({ children, formId, onSubmit }) {
  const debug = useFormDebug();
  const { permissions, permittedByDefault = false } = useFormPermissions();
  const { formSchema } = useFormSchema();
  const { formDefaultValues, formWarnings = [] } = useFormDefaultValues();
  const { formValidationSchema: validationSchema } = useFormValidationSchema();

  const [state, setState] = useState(INITIAL_STATE);

  const initialize = async () => {
    setState({ ...INITIAL_STATE, loading: true });
    try {
      const newSchema = await addFormSchemaEssentials({
        schema: formSchema,
        permissions,
        initialValues: formDefaultValues,
        permittedByDefault,
      });

      const removeFieldsFromValidation = getMissingFields(
        getYupLeafFieldPaths(validationSchema),
        getNamesFromSchema(newSchema),
      );

      let validationSchemaTemp = validationSchema;
      removeFieldsFromValidation.forEach(v => {
        validationSchemaTemp = validationSchemaTemp.omit(v);
      });

      setState({
        validations: validationSchemaTemp,
        schema: newSchema,
        errors: [],
        loading: false,
      });

      debug.add('FormComposerProvider Validation', validationSchemaTemp);
      debug.add('FormComposerProvider Schema Received', formSchema);
      debug.add('FormComposerProvider Schema Generated', newSchema);
      debug.add('FormComposerProvider DefaultValues', formDefaultValues);
    } catch (err) {
      setState({
        ...INITIAL_STATE,
        errors: [{ message: err.message }],
        loading: false,
      });
    }
  };

  useEffect(() => {
    debug.addStep('FormComposer');
    initialize();
  }, [formId]);

  const value = useMemo(
    () => ({
      validations: state?.validations,
      schema: state?.schema,
      defaultValues: formDefaultValues,
      permissions,
      formId,
      onSubmit,
      formWarnings,
    }),
    [
      formDefaultValues,
      permissions,
      formId,
      validationSchema,
      state.validations,
      state.schema,
      formWarnings,
    ],
  );

  return (
    <FormComposerContext.Provider value={value}>
      <FormProviderRenderer
        message="Composing form"
        loading={state?.loading}
        errors={state?.errors}>
        {children}
      </FormProviderRenderer>
    </FormComposerContext.Provider>
  );
}

export const useFormComposer = () => {
  return useContext(FormComposerContext);
};
